/**
 * NAV SYSTEM (DESKTOP)
 * =====================================================================
 * State controller for the full-page navigation surface.
 *
 * Architecture:
 *   Layer 1 — Header Shell       (existing, persistent, never animates)
 *   Layer 2 — Navigation Surface (this controller drives it)
 *   Layer 3 — Navigation Content (staged reveal, Phase 4)
 *   Layer 4 — Dual Wordmark      (coordinated illusion, Phase 3)
 *
 * State classes on <body>:
 *   .nav-closed  — default
 *   .nav-opening — surface expanding (Phase 2+)
 *   .nav-open    — fully open
 *   .nav-closing — surface collapsing (Phase 2+)
 *
 * Phase 2: scaleY surface animation added.
 *   Dual wordmark  → Phase 3
 *   Content reveal → Phase 4
 *   Timing/easing  → Phase 5
 *   Real nav data  → Phase 6
 * =====================================================================
 */

(function () {
  'use strict';

  // Must match CSS transition duration
  var SURFACE_MS = 170;

  function isDesktop() {
    return window.innerWidth > 900;
  }

  var body = document.body;
  var surface = document.getElementById('nav-surface');

  if (!surface) return;

  // ----------------------------------------------------------------
  // STATE ENGINE
  // ----------------------------------------------------------------
  var NavSystem = {
    _state: 'closed',
    _activeHandle: null,
    _openTimer: null,
    _closeTimer: null,

    get state() { return this._state; },

    _applyState: function (s) {
      this._state = s;
      body.classList.remove('nav-closed', 'nav-opening', 'nav-open', 'nav-closing');
      body.classList.add('nav-' + s);
    },

    // ----------------------------------------------------------------
    // OPEN
    // Applies nav-opening immediately (surface begins expanding).
    // Settles to nav-open after the surface animation completes.
    // Clicking the same trigger again closes (toggle).
    // ----------------------------------------------------------------
    open: function (handle) {
      if (!isDesktop()) return;

      // Toggle: clicking the active trigger collapses the surface
      if ((this._state === 'open' || this._state === 'opening') && this._activeHandle === handle) {
        this.close();
        return;
      }

      // Cancel any in-progress close
      clearTimeout(this._closeTimer);

      this._activeHandle = handle;
      this._applyState('opening');
      surface.setAttribute('aria-hidden', 'false');
      this._lockScroll(true);
      this._syncTriggers(handle);
      this._activatePanel(handle);

      // Settle to fully open after surface expands
      var self = this;
      this._openTimer = setTimeout(function () {
        if (self._state === 'opening') {
          self._applyState('open');
        }
      }, SURFACE_MS);
    },

    // ----------------------------------------------------------------
    // CLOSE
    // Applies nav-closing immediately (surface begins collapsing).
    // Settles to nav-closed after animation completes.
    // ----------------------------------------------------------------
    close: function () {
      // Cancel any in-progress open
      clearTimeout(this._openTimer);

      this._applyState('closing');
      surface.setAttribute('aria-hidden', 'true');
      this._lockScroll(false);
      this._syncTriggers(null);

      // Settle to fully closed after surface collapses
      var self = this;
      this._closeTimer = setTimeout(function () {
        if (self._state === 'closing') {
          self._activeHandle = null;
          self._applyState('closed');
          self._activatePanel(null);
        }
      }, SURFACE_MS);
    },

    // ----------------------------------------------------------------
    // SCROLL LOCK
    // ----------------------------------------------------------------
    _lockScroll: function (lock) {
      document.documentElement.style.overflow = lock ? 'hidden' : '';
      document.body.style.overflow = lock ? 'hidden' : '';
    },

    // ----------------------------------------------------------------
    // SYNC TRIGGER ARIA STATES
    // ----------------------------------------------------------------
    _syncTriggers: function (handle) {
      document.querySelectorAll('.ssda-nav-trigger').forEach(function (t) {
        var isActive = handle !== null && t.dataset.handle === handle;
        t.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });
    },

    // ----------------------------------------------------------------
    // ACTIVATE PANEL
    // Shows the panel whose data-handle matches the active trigger.
    // All other panels are hidden. Pass null to hide all.
    // ----------------------------------------------------------------
    _activatePanel: function (handle) {
      surface.querySelectorAll('.ns-panel').forEach(function (panel) {
        panel.classList.toggle('is-active', panel.dataset.handle === handle && handle !== null);
      });
    }
  };

  // ----------------------------------------------------------------
  // INIT
  // ----------------------------------------------------------------
  function init() {
    // Set initial body state class
    NavSystem._applyState('closed');

    // ---- Wire nav triggers ----
    // Clone existing triggers to strip the mega-panel handlers bound
    // by the inline script in header.liquid, then re-bind to NavSystem.
    document.querySelectorAll('li.has-mega > .ssda-nav-trigger').forEach(function (trigger) {
      var fresh = trigger.cloneNode(true);
      trigger.parentNode.replaceChild(fresh, trigger);

      fresh.setAttribute('aria-haspopup', 'dialog');
      fresh.setAttribute('aria-expanded', 'false');

      fresh.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        NavSystem.open(fresh.dataset.handle);
      });

      fresh.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          NavSystem.open(fresh.dataset.handle);
        } else if (e.key === 'Escape') {
          NavSystem.close();
          fresh.focus();
        }
      });
    });

    // ---- Close button inside the surface ----
    surface.querySelectorAll('[data-ns-action="close"]').forEach(function (btn) {
      btn.addEventListener('click', function () { NavSystem.close(); });
    });

    // ---- Escape key (global) ----
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && (NavSystem.state === 'open' || NavSystem.state === 'opening')) {
        NavSystem.close();
      }
    });

    // ---- Resize: close if viewport drops below desktop breakpoint ----
    window.addEventListener('resize', function () {
      if (!isDesktop() && NavSystem.state !== 'closed' && NavSystem.state !== 'closing') {
        NavSystem.close();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for phase-by-phase debugging in console
  window.NavSystem = NavSystem;

})();
